import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetch} from "services/query-client";
import {GlobeAltIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {EyeIcon, PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {truncate} from "services/string";
import Dropdown from "components/Dropdown";
import {Organisation} from "types/Organisation";
import {useNavigate} from "react-router-dom";
import DeleteOrganisation from '../Delete';
import Link from "components/Link";
import {useOperator} from "../../../context/OperatorContext";

function OrganisationList() {
    const navigate = useNavigate();
    const operator = useOperator();

    const [deletingOrganisation, setDeletingOrganisation] = useState<Organisation | null>(null);
    const [page, setPage] = useState<number>(1);

    const result = useQuery(
        ['organisations.all', {page}],
        () => ngage.organisations.filtered({page}),
        {keepPreviousData: true}
    )

    // Prefetch next and previous pages
    prefetch('organisations.all', {page}, ngage.organisations.filtered)

    // Prepare page actions
    const actions = <Button icon={PlusIcon} onClick={() => navigate('/organisations/new')}>New</Button>

    const breadcrumbs = {'Admin': '/', 'Organisations': '/organisations'}

    return (
        <Page
          title="Organisations"
          subTitle={`Organisations connected to ${operator?.operator?.name}`}
          actions={actions}
          breadcrumbs={breadcrumbs}
        >
            <Table paginationData={result.data} setPage={setPage}>
                <TableHead>
                    <TableRow>
                        <TableHeader>Name</TableHeader>
                        <TableHeader>Email</TableHeader>
                        <TableHeader>Phone</TableHeader>
                        <TableHeader alignment={TableHeader.alignments.RIGHT}>Members</TableHeader>
                        <TableHeader>Added</TableHeader>
                        <TableHeader/>
                    </TableRow>
                </TableHead>

                <TableBody isLoading={result.isLoading}>

                    {result?.data && result.data.data.length < 1?
                        <TableRow index={0}>
                            <TableData colSpan={6}>
                                <div className="py-4 flex flex-col justify-center items-center">
                                    <GlobeAltIcon className="h-8 w-8 text-gray-400 mb-2"/>
                                    <span className="text-sm font-medium text-gray-600">No organisations</span>
                                </div>
                            </TableData>
                        </TableRow>
                    : result?.data?.data.map((organisation, index) => (
                        <TableRow key={organisation.id} index={index} highlight>
                            <TableData>
                                <div className="flex">
                                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                                        {/* TODO: Convert to component */}
                                        <Dropdown position="left">
                                            {[
                                                {name: 'View', icon: EyeIcon, onClick: () => navigate(`/organisations/${organisation.id}`)},
                                                {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/organisations/${organisation.id}/edit`)},
                                                {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingOrganisation(organisation)}
                                            ]}
                                        </Dropdown>
                                    </div>

                                    <div className="flex group-hover:translate-x-1 transition-all">
                                        <GlobeAltIcon className="h-5 w-5 mr-2 text-gray-500" /> {truncate(organisation.name, 35)}
                                    </div>
                                </div>
                            </TableData>
                            <TableData>
                                {organisation.email && <Link to={`mailto:${organisation.email}`}>{organisation.email}</Link>}
                            </TableData>
                            <TableData>
                                {organisation.phone && <Link to={`tel:${organisation.phone}`}>{organisation.phone}</Link>}
                            </TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>{organisation.member_count}</TableData>
                            <TableData>{formattedDateTime(organisation.created_at)}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>
                                {/* TODO: Convert to component */}
                                <Dropdown>
                                    {[
                                        {name: 'View', icon: EyeIcon, onClick: () => navigate(`/organisations/${organisation.id}`)},
                                        {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/organisations/${organisation.id}/edit`)},
                                        {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingOrganisation(organisation)}
                                    ]}
                                </Dropdown>
                            </TableData>

                            {/* Delete modal */}
                            <DeleteOrganisation
                                organisation={deletingOrganisation}
                                show={organisation === deletingOrganisation}
                                onCancel={() => setDeletingOrganisation(null)}
                                onDelete={() => navigate('/organisations')}
                                onDeleteFail={() => setDeletingOrganisation(null)}
                            />
                        </TableRow>
                    ))}

                </TableBody>
            </Table>
        </Page>
    );
}

export default OrganisationList;